import React, { useState } from 'react';
import { User, Mail, Shield, Clock, Edit3, X, Save, Loader2, HelpCircle, Lock, Eye, EyeOff } from 'lucide-react';
import Tooltip from '../components/UI/Tooltip';
import { User as UserType } from '../services/authService';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { toast } from 'react-hot-toast';

interface ProfileProps {
  user: UserType;
}

interface ProfileFormData {
  firstname: string;
  lastname: string;
  email: string;
  timezone: string;
}

const Profile: React.FC<ProfileProps> = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // displayUser is the user data shown on the left side - only updates after successful save
  const [displayUser, setDisplayUser] = useState<UserType>(user);
  
  // formData is the editable data in the form - updates as user types
  const [formData, setFormData] = useState<ProfileFormData>({
    firstname: user.firstname || '',
    lastname: user.lastname || '',
    email: user.email || '',
    timezone: user.time_zone || 'US/Eastern' // Default timezone
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Handles saving profile changes
   * Updates the profile via PATCH request, then fetches fresh data to ensure UI reflects changes
   */
  const handleSave = async () => {
    setIsLoading(true);
    try {
      // First, update the profile
      const profileData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        time_zone: formData.timezone
      }
      const updateResponse = await apiService.updateProfile(profileData);
      if (!updateResponse.errors) {
        // Then fetch the fresh profile data from the server
        const freshProfile = await apiService.getMyProfile();
        
        // API returns data in 'result' property, not 'data'
        const profileData = freshProfile.result || freshProfile.data;
        
        if (!freshProfile.errors && profileData) {
          // Update the auth service with the fresh data from server
          authService.setUser(profileData);
          
          // Update the display user (left side) with the fresh data
          setDisplayUser(profileData);
          
          // Update local form data to match what's on the server
          setFormData({
            firstname: profileData.firstname || '',
            lastname: profileData.lastname || '',
            email: profileData.email || '',
            timezone: profileData.time_zone || 'US/Eastern'
          });
          
          toast.success('Profile updated successfully!');
          setIsEditing(false);
        } else {
          toast.error('Profile updated but failed to fetch fresh data. Please refresh the page.');
        }
      } else {
        toast.error('Failed to update profile: ' + updateResponse.message);
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile: ' + (error?.response?.data?.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      email: user.email || '',
      timezone: user.time_zone || 'US/Eastern'
    });
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const response = await apiService.updateProfile({
        current_password: passwordData.currentPassword,
        password: passwordData.newPassword,
      });
      if (!response.errors) {
        toast.success('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowNewPassword(false);
        setShowConfirmPassword(false);
      } else {
        setPasswordError(response.message || 'Failed to change password.');
      }
    } catch (error: any) {
      setPasswordError(error?.response?.data?.message || 'Failed to change password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <p className="mt-2 text-gray-600">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-center">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-12 w-12 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {displayUser.firstname && displayUser.lastname 
                  ? `${displayUser.firstname} ${displayUser.lastname}` 
                  : displayUser.email?.split('@')[0] || 'User'}
              </h2>
              <p className="text-gray-600 mt-1 break-words max-w-full px-2">{displayUser.email}</p>
              <div className="mt-4 flex items-center justify-center space-x-2 flex-wrap gap-1">
                {user?.auth?.admin_group && (
                  <Tooltip
                    content="You can contact ITP on behalf of the account for any matter including account changes, billing inquiries, and service requests."
                    placement="bottom"
                  >
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-help">
                      Account Authorized
                    </span>
                  </Tooltip>
                )}
                {user?.auth?.billing_group && (
                  <Tooltip
                    content="You can contact ITP regarding billing matters including invoices, payments, and billing disputes."
                    placement="bottom"
                  >
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-help">
                      Billing Authorized
                    </span>
                  </Tooltip>
                )}
                {user?.auth?.technical_group && (
                  <Tooltip
                    content="You can approve technical changes such as network modifications, server updates, and service configurations."
                    placement="bottom"
                  >
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 cursor-help">
                      Technical Authorized
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
              {!isEditing && (
                <button
                  onClick={handleEditClick}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit Profile
                </button>
              )}
            </div>
            
            <div className="space-y-6">
              {/* First Name */}
              <div className="flex items-start space-x-3">
                <User className="h-5 w-5 text-gray-400 mt-1" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter your first name"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{formData.firstname || 'Not set'}</p>
                  )}
                </div>
              </div>

              {/* Last Name */}
              <div className="flex items-start space-x-3">
                <User className="h-5 w-5 text-gray-400 mt-1" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter your last name"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{formData.lastname || 'Not set'}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-gray-400 mt-1" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  {isEditing ? (
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter your email address"
                      />
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-amber-800">
                              Important: Login Credential Change
                            </h3>
                            <div className="mt-1 text-sm text-amber-700">
                              <p>
                                Changing your email address will also change your username for logging into the portal. 
                                Make sure to remember your new email when logging in next time.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 text-gray-900 break-words">{formData.email}</p>
                  )}
                </div>
              </div>

              {/* Timezone */}
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-gray-400 mt-1" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Timezone</label>
                  {isEditing ? (
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="US/Eastern">US/Eastern</option>
                      <option value="US/Central">US/Central</option>
                      <option value="US/Mountain">US/Mountain</option>
                      <option value="US/Pacific">US/Pacific</option>
                      <option value="UTC">UTC</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">{formData.timezone}</p>
                  )}
                </div>
              </div>

              {/* Authorizations (Read-only) */}
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Authorizations</label>
                  <div className="mt-1 space-y-1">
                    <p className="text-sm text-gray-600">
                      • Portal Access: <span className="text-green-600 font-medium">Enabled</span>
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      • Account Authorized: <span className={user?.auth?.admin_group ? "text-green-600 font-medium ml-1" : "text-gray-400 font-medium ml-1"}>
                        {user?.auth?.admin_group ? "Yes" : "No"}
                      </span>
                      <Tooltip content="Can contact ITP on behalf of the account for any matter" placement="right">
                        <HelpCircle className="h-3.5 w-3.5 text-gray-400 ml-1 cursor-help" />
                      </Tooltip>
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      • Billing Authorized: <span className={user?.auth?.billing_group ? "text-green-600 font-medium ml-1" : "text-gray-400 font-medium ml-1"}>
                        {user?.auth?.billing_group ? "Yes" : "No"}
                      </span>
                      <Tooltip content="Can contact ITP regarding billing matters including invoices and payments" placement="right">
                        <HelpCircle className="h-3.5 w-3.5 text-gray-400 ml-1 cursor-help" />
                      </Tooltip>
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      • Technical Authorized: <span className={user?.auth?.technical_group ? "text-green-600 font-medium ml-1" : "text-gray-400 font-medium ml-1"}>
                        {user?.auth?.technical_group ? "Yes" : "No"}
                      </span>
                      <Tooltip content="Can approve technical changes such as network modifications and service configurations" placement="right">
                        <HelpCircle className="h-3.5 w-3.5 text-gray-400 ml-1 cursor-help" />
                      </Tooltip>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="mt-8 pt-6 border-t border-gray-200 flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="lg:col-span-2 lg:col-start-2">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-6">
              <Lock className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
            </div>

            {passwordError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {passwordError}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Signed in with Microsoft or Google and never set one? Use "Forgot password" on the login page.</p>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter new password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Confirm new password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {passwordLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
